import { computed } from 'vue'
import {
  COORDINATION_BASE_ROLES,
  coordinationEnabledForVersion,
  type CoordinationBaseRole,
  type CoordinationBasis,
} from '@arcexperts/core-types'
import { useProjectStore } from '@/stores/project'
import { useContentStore } from '@/stores/content'
import { formatHours } from '@/helpers/format'
import { fillPlaceholders } from '@/helpers/text-placeholders'
import type { SetupSummaryListItems } from '@/types'

export function useCoordination() {
  const projectStore = useProjectStore()
  const content = useContentStore()
  const enabled = computed(() =>
    coordinationEnabledForVersion(
      projectStore.getVersion('specialist_coordination_setup'),
      content.coordinationSettings
    )
  )
  const disciplines = computed(() =>
    COORDINATION_BASE_ROLES.filter((role) => projectStore.getMeta?.parts?.includes(role)).map((role) => {
      const part = projectStore.getProject?.[`${role.toLowerCase()}_setup`]
      const hours = part?.calculatedResults?.results?.estimatedTotalHours
      return {
        role,
        hours: typeof hours === 'number' && Number.isFinite(hours) && hours >= 0 ? hours : 0,
        missing: typeof hours !== 'number',
        stored: !projectStore.getValidRoles.includes(role),
        calculatedAt: part?.calculatedResults?.calculatedAt as number | undefined,
      }
    })
  )
  return { enabled, disciplines, settings: computed(() => content.coordinationSettings) }
}

function roleNames(roles: CoordinationBaseRole[]): string {
  const content = useContentStore()
  return roles
    .map((role) => content.getWidgetText('widget-texts-general', undefined, 'roleParts', role) || role)
    .join(', ')
}

/**
 * Why the surcharge is 0 h (0 % or no discipline selected), otherwise null.
 * Shown instead of the formula in the result boxes.
 */
export function coordinationZeroReason(
  percent: number,
  roleCount: number,
  wt: (...path: string[]) => string
): string | null {
  if (percent === 0) return wt('coordination', 'zero')
  if (roleCount === 0) return wt('coordination', 'none')
  return null
}

/** "4 % von 3'455 h (Heizung/Kälte, Sanitär)", shown on the Leistung tab. */
export function coordinationOrigin(basis: CoordinationBasis, wt: (...path: string[]) => string): string {
  const reason = coordinationZeroReason(basis.percent, basis.roles.length, wt)
  if (reason) return reason
  return fillPlaceholders(wt('coordination', 'origin'), {
    percent: String(basis.percent),
    hours: formatHours(basis.baseHours, 0),
    roles: roleNames(basis.roles),
  })
}

export function renderCoordinationSummary(
  basis: CoordinationBasis,
  hours: number,
  justification: string | undefined,
  wt: (...path: string[]) => string
): SetupSummaryListItems {
  return {
    usageSections: [
      {
        title: wt('coordination', 'title'),
        items: [
          {
            title: wt('coordination', 'base'),
            value: `${formatHours(basis.baseHours, 0)} h`,
            remark: roleNames(basis.roles),
          },
          { title: wt('coordination', 'percent'), value: `${basis.percent} %` },
          // Same as the quality factor justifications: user text goes into the escaped title, never the HTML value.
          ...(justification?.trim()
            ? [{ title: justification.trim(), value: '', additionalClass: 'justification-text' }]
            : []),
          {
            title: wt('coordination', 'result'),
            value: `${formatHours(hours, 0)} h`,
            additionalClass: 'highlight-item',
          },
        ],
      },
    ],
    riskList: [],
    costList: [],
  }
}
